import * as React from "react"
import * as DropdownMenuPrimitive from "@radix-ui/react-dropdown-menu"
import { Check, ChevronRight, Circle } from "lucide-react"

import { cn } from "../../lib/utils"
import { announceToScreenReader } from "../../lib/accessibility"

const DropdownMenu = DropdownMenuPrimitive.Root

const DropdownMenuTrigger = React.forwardRef(({ className, "aria-label": ariaLabel, ...props }, ref) => (
  <DropdownMenuPrimitive.Trigger
    ref={ref}
    aria-label={ariaLabel}
    aria-haspopup="menu"
    className={cn(
      // Enhanced focus indicators for WCAG 2.1 AA compliance
      "focus:outline-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-purple-500 focus-visible:ring-offset-2 focus-visible:ring-offset-background",
      "transition-colors",
      className
    )}
    {...props}
  />
))
DropdownMenuTrigger.displayName = DropdownMenuPrimitive.Trigger.displayName

const DropdownMenuGroup = DropdownMenuPrimitive.Group

const DropdownMenuPortal = DropdownMenuPrimitive.Portal

const DropdownMenuSub = DropdownMenuPrimitive.Sub

const DropdownMenuRadioGroup = DropdownMenuPrimitive.RadioGroup

const DropdownMenuSubTrigger = React.forwardRef(({ className, inset, children, ...props }, ref) => (
  <DropdownMenuPrimitive.SubTrigger
    ref={ref}
    aria-haspopup="menu"
    className={cn(
      "flex cursor-default select-none items-center gap-2 rounded-sm px-2 py-1.5 text-sm outline-none",
      // Enhanced focus states
      "focus:bg-accent focus:outline-none",
      "focus-visible:bg-purple-600/20 focus-visible:ring-2 focus-visible:ring-purple-500 focus-visible:ring-inset",
      "data-[state=open]:bg-accent [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
      inset && "pl-8",
      className
    )}
    {...props}>
    {children}
    <ChevronRight className="ml-auto" aria-hidden="true" />
  </DropdownMenuPrimitive.SubTrigger>
))
DropdownMenuSubTrigger.displayName =
  DropdownMenuPrimitive.SubTrigger.displayName

const DropdownMenuSubContent = React.forwardRef(({ className, ...props }, ref) => (
  <DropdownMenuPrimitive.SubContent
    ref={ref}
    role="menu"
    className={cn(
      "z-50 min-w-[8rem] overflow-hidden rounded-md border bg-popover p-1 text-popover-foreground shadow-lg",
      "data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 origin-[--radix-dropdown-menu-content-transform-origin]",
      // Focus trap styling
      "focus:outline-none",
      className
    )}
    {...props} />
))
DropdownMenuSubContent.displayName =
  DropdownMenuPrimitive.SubContent.displayName

const DropdownMenuContent = React.forwardRef(({ className, sideOffset = 4, onCloseAutoFocus, ...props }, ref) => {
  // Handle focus return to trigger when closed
  const handleCloseAutoFocus = React.useCallback((event) => {
    // Announce to screen readers that menu is closed
    announceToScreenReader('Menu closed', 'polite');
    if (onCloseAutoFocus) {
      onCloseAutoFocus(event);
    }
  }, [onCloseAutoFocus]);

  // Announce when menu opens
  const handleOpenAutoFocus = React.useCallback(() => {
    announceToScreenReader('Menu opened. Use arrow keys to navigate.', 'polite');
  }, []);

  return (
    <DropdownMenuPrimitive.Portal>
      <DropdownMenuPrimitive.Content
        ref={ref}
        sideOffset={sideOffset}
        role="menu"
        aria-orientation="vertical"
        onOpenAutoFocus={handleOpenAutoFocus}
        onCloseAutoFocus={handleCloseAutoFocus}
        className={cn(
          "z-50 max-h-[var(--radix-dropdown-menu-content-available-height)] min-w-[8rem] overflow-y-auto overflow-x-hidden rounded-md border bg-popover p-1 text-popover-foreground shadow-md",
          "data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 origin-[--radix-dropdown-menu-content-transform-origin]",
          // Focus trap styling
          "focus:outline-none",
          className
        )}
        {...props} />
    </DropdownMenuPrimitive.Portal>
  );
})
DropdownMenuContent.displayName = DropdownMenuPrimitive.Content.displayName

const DropdownMenuItem = React.forwardRef(({ className, inset, onSelect, children, ...props }, ref) => {
  // Announce selection to screen readers
  const handleSelect = React.useCallback((event) => {
    if (typeof children === 'string') {
      announceToScreenReader(`${children} activated`, 'polite');
    }
    onSelect?.(event);
  }, [children, onSelect]);

  return (
    <DropdownMenuPrimitive.Item
      ref={ref}
      role="menuitem"
      onSelect={handleSelect}
      className={cn(
        "relative flex cursor-default select-none items-center gap-2 rounded-sm px-2 py-1.5 text-sm outline-none transition-colors",
        // Enhanced focus indicators for keyboard navigation
        "focus:bg-accent focus:text-accent-foreground focus:outline-none",
        "focus-visible:bg-purple-600/20 focus-visible:text-white focus-visible:ring-2 focus-visible:ring-purple-500 focus-visible:ring-inset",
        // Highlighted state (keyboard navigation)
        "data-[highlighted]:bg-accent data-[highlighted]:text-accent-foreground",
        // Disabled state
        "data-[disabled]:pointer-events-none data-[disabled]:opacity-50",
        "[&>svg]:size-4 [&>svg]:shrink-0",
        inset && "pl-8",
        className
      )}
      {...props}>
      {children}
    </DropdownMenuPrimitive.Item>
  );
})
DropdownMenuItem.displayName = DropdownMenuPrimitive.Item.displayName

const DropdownMenuCheckboxItem = React.forwardRef(({ className, children, checked, onCheckedChange, ...props }, ref) => {
  // Announce state change to screen readers
  const handleCheckedChange = React.useCallback((newChecked) => {
    const state = newChecked ? 'checked' : 'unchecked';
    if (typeof children === 'string') {
      announceToScreenReader(`${children} ${state}`, 'polite');
    }
    onCheckedChange?.(newChecked);
  }, [children, onCheckedChange]);

  return (
    <DropdownMenuPrimitive.CheckboxItem
      ref={ref}
      role="menuitemcheckbox"
      aria-checked={checked}
      className={cn(
        "relative flex cursor-default select-none items-center rounded-sm py-1.5 pl-8 pr-2 text-sm outline-none transition-colors",
        // Enhanced focus indicators
        "focus:bg-accent focus:text-accent-foreground focus:outline-none",
        "focus-visible:bg-purple-600/20 focus-visible:ring-2 focus-visible:ring-purple-500 focus-visible:ring-inset",
        "data-[highlighted]:bg-accent data-[highlighted]:text-accent-foreground",
        "data-[disabled]:pointer-events-none data-[disabled]:opacity-50",
        className
      )}
      checked={checked}
      onCheckedChange={handleCheckedChange}
      {...props}>
      <span className="absolute left-2 flex h-3.5 w-3.5 items-center justify-center">
        <DropdownMenuPrimitive.ItemIndicator>
          <Check className="h-4 w-4" aria-hidden="true" />
        </DropdownMenuPrimitive.ItemIndicator>
      </span>
      {children}
    </DropdownMenuPrimitive.CheckboxItem>
  );
})
DropdownMenuCheckboxItem.displayName =
  DropdownMenuPrimitive.CheckboxItem.displayName

const DropdownMenuRadioItem = React.forwardRef(({ className, children, onSelect, ...props }, ref) => {
  // Announce selection to screen readers
  const handleSelect = React.useCallback((event) => {
    if (typeof children === 'string') {
      announceToScreenReader(`${children} selected`, 'polite');
    }
    onSelect?.(event);
  }, [children, onSelect]);

  return (
    <DropdownMenuPrimitive.RadioItem
      ref={ref}
      role="menuitemradio"
      onSelect={handleSelect}
      className={cn(
        "relative flex cursor-default select-none items-center rounded-sm py-1.5 pl-8 pr-2 text-sm outline-none transition-colors",
        // Enhanced focus indicators
        "focus:bg-accent focus:text-accent-foreground focus:outline-none",
        "focus-visible:bg-purple-600/20 focus-visible:ring-2 focus-visible:ring-purple-500 focus-visible:ring-inset",
        "data-[highlighted]:bg-accent data-[highlighted]:text-accent-foreground",
        "data-[disabled]:pointer-events-none data-[disabled]:opacity-50",
        className
      )}
      {...props}>
      <span className="absolute left-2 flex h-3.5 w-3.5 items-center justify-center">
        <DropdownMenuPrimitive.ItemIndicator>
          <Circle className="h-2 w-2 fill-current" aria-hidden="true" />
        </DropdownMenuPrimitive.ItemIndicator>
      </span>
      {children}
    </DropdownMenuPrimitive.RadioItem>
  );
})
DropdownMenuRadioItem.displayName = DropdownMenuPrimitive.RadioItem.displayName

const DropdownMenuLabel = React.forwardRef(({ className, inset, ...props }, ref) => (
  <DropdownMenuPrimitive.Label
    ref={ref}
    className={cn("px-2 py-1.5 text-sm font-semibold", inset && "pl-8", className)}
    {...props} />
))
DropdownMenuLabel.displayName = DropdownMenuPrimitive.Label.displayName

const DropdownMenuSeparator = React.forwardRef(({ className, ...props }, ref) => (
  <DropdownMenuPrimitive.Separator
    ref={ref}
    role="separator"
    className={cn("-mx-1 my-1 h-px bg-muted", className)}
    {...props} />
))
DropdownMenuSeparator.displayName = DropdownMenuPrimitive.Separator.displayName

const DropdownMenuShortcut = ({
  className,
  ...props
}) => {
  return (
    <span
      className={cn("ml-auto text-xs tracking-widest opacity-60", className)}
      aria-hidden="true"
      {...props} />
  );
}
DropdownMenuShortcut.displayName = "DropdownMenuShortcut"

export {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuCheckboxItem,
  DropdownMenuRadioItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuShortcut,
  DropdownMenuGroup,
  DropdownMenuPortal,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuRadioGroup,
}
